import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchAllTutorials,
  removeTutorial,
  toggleTutorialActive,
  reorderTutorial,
  selectTutorials,
  selectLoading
} from '../../../../app/store/slices/adminSlice';
import { CircularProgress, IconButton, Tooltip, Chip } from '@mui/material';
import { FiEdit2, FiTrash2, FiEye, FiEyeOff, FiArrowUp, FiArrowDown, FiPlus, FiPlay } from 'react-icons/fi';
import { toast } from 'react-toastify';
import TutorialFormModal from './components/TutorialFormModal';
import TutorialReorderModal from './components/TutorialReorderModal';
import TutorialVideoModal from './components/TutorialVideoModal';
import ConfirmationModal from '../../../shared/components/modals/ConfirmationModal';
import {LEVEL_COLORS} from '../../../../utils/constants/dashboardConstants'


const TutorialManagement = () => {
  const dispatch = useDispatch();
  const tutorialsState = useSelector(selectTutorials) || { tutorials: [] };
  const tutorials = tutorialsState.tutorials || [];
  const loading = useSelector(selectLoading);
  const [searchTerm, setSearchTerm] = useState('');
  const [category, setCategory] = useState('');
  const [level, setLevel] = useState('');
  const pageSize = 1000;

  const [formModalOpen, setFormModalOpen] = useState(false);
  const [reorderModalOpen, setReorderModalOpen] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [editingTutorial, setEditingTutorial] = useState(null);
  const [viewingTutorial, setViewingTutorial] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, tutorial: null });
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!searchTerm && !category && !level) {
      dispatch(fetchAllTutorials({
        page: 1,
        limit: pageSize,
        sortBy: 'sequence',
        sortOrder: 'asc'
      }));
    } else {
      dispatch(fetchAllTutorials({
        page: 1,
        limit: pageSize,
        searchTerm,
        category,
        level,
        sortBy: 'sequence',
        sortOrder: 'asc'
      }));
    }
  }, [dispatch, searchTerm, category, level]);

  const handleCreate = () => {
    setEditingTutorial(null);
    setFormModalOpen(true);
  };

  const handleEdit = (tutorial) => {
    setEditingTutorial(tutorial);
    setFormModalOpen(true);
  };

  const handleView = (tutorial) => {
    setViewingTutorial(tutorial);
    setVideoModalOpen(true);
  };

  const handleDelete = (tutorial) => {
    setDeleteModal({ open: true, tutorial });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.tutorial) return;

    setIsDeleting(true);
    try {
      await dispatch(removeTutorial(deleteModal.tutorial.id)).unwrap();
      toast.success('Tutorial deleted successfully');
      setDeleteModal({ open: false, tutorial: null });
    } catch (error) {
      toast.error(`Failed to delete tutorial: ${error}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleReorder = () => {
    setReorderModalOpen(true);
  };

  const handleFormSuccess = () => {
    setFormModalOpen(false);
    setEditingTutorial(null);
  };

  const handleReorderSuccess = () => {
    setReorderModalOpen(false);
    dispatch(fetchAllTutorials({
      page: 1,
      limit: pageSize,
      searchTerm,
      category,
      level,
      sortBy: 'sequence',
      sortOrder: 'asc'
    }));
  };

  const formatDuration = (duration) => {
    if (!duration) return 'N/A';
    return duration;
  };

  const getLevelBadge = (level) => {
    if (!level) return null;
    return (
      <Chip 
        label={level.charAt(0).toUpperCase() + level.slice(1)} 
        color={LEVEL_COLORS[level] || 'default'}
        size="small"
      />
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-btn-primary">Tutorial Management</h2>
        <div className="flex gap-2">
          <button
            onClick={handleReorder}
            className="px-4 py-2 bg-btn-secondary text-btn-secondary-text rounded-lg hover:bg-btn-secondary-hover transition-colors flex items-center gap-2"
          >
            <FiArrowUp />
            Reorder
          </button>
          <button
            onClick={handleCreate}
            className="px-4 py-2 bg-btn-primary text-btn-primary-text rounded-lg hover:bg-btn-primary-hover transition-colors flex items-center gap-2"
          >
            <FiPlus />
            Add Tutorial
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <input
              type="text"
              placeholder="Search tutorials..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-btn-primary"
            />
          </div>
          <div>
            <input
              type="text"
              placeholder="Filter by category..."
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-btn-primary"
            />
          </div>
          <div>
            <select
              value={level}
              onChange={(e) => setLevel(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-btn-primary"
            >
              <option value="">All Levels</option>
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tutorial List */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <CircularProgress />
        </div>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sequence
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Title
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Level
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Duration
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {tutorials.map((tutorial) => (
                    <tr key={tutorial.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {tutorial.sequence}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div className="max-w-xs">
                          <div className="font-medium flex items-center gap-2">
                            {tutorial.title}
                            <button
                              onClick={() => handleView(tutorial)}
                              className="text-btn-primary hover:text-btn-primary-hover transition-colors"
                              title="Play video"
                            >
                              <FiPlay size={16} />
                            </button>
                          </div>
                          <div className="text-gray-500 text-xs truncate">{tutorial.description}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {tutorial.category || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {getLevelBadge(tutorial.level)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDuration(tutorial.duration)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center gap-2">
                          <Tooltip title="Play Video">
                            <IconButton
                              size="small"
                              onClick={() => handleView(tutorial)}
                              className="text-gray-600 hover:text-btn-primary"
                            >
                              <FiPlay />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(tutorial)}
                              className="text-gray-600 hover:text-btn-primary"
                            >
                              <FiEdit2 />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(tutorial)}
                              className="text-gray-600 hover:text-red-600"
                            >
                              <FiTrash2 />
                            </IconButton>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Modals */}
      <TutorialFormModal
        open={formModalOpen}
        onClose={() => setFormModalOpen(false)}
        tutorial={editingTutorial}
        onSuccess={handleFormSuccess}
      />

      <TutorialReorderModal
        open={reorderModalOpen}
        onClose={() => setReorderModalOpen(false)}
        onSuccess={handleReorderSuccess}
      />

      <TutorialVideoModal
        open={videoModalOpen}
        onClose={() => setVideoModalOpen(false)}
        tutorial={viewingTutorial}
      />

      <ConfirmationModal
        open={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, tutorial: null })}
        onConfirm={handleDeleteConfirm}
        title="Delete Tutorial"
        message={`Are you sure you want to delete "${deleteModal.tutorial?.title}"? This action cannot be undone.`}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default TutorialManagement;